/**
 * stableFluidPipeline — Stam's stable 2D fluid simulation.
 *
 * Real ping-pong fluid simulation:
 *   - Two 256x256 RGBA textures store velocity (RG) and dye (BA).
 *   - Each frame: advect velocity, diffuse, project (pressure solve),
 *     then advect dye.
 *   - The render pass visualizes the dye.
 *
 * This is the Jos Stam "Stable Fluids" paper, implemented via
 * fragment shaders with ping-pong textures. The 2D divergence +
 * pressure solve uses Jacobi iterations.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface StableFluidPipeline {
  programs: Record<string, WebGLProgram | null>;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  velTextures: [WebGLTexture | null, WebGLTexture | null];
  dyeTextures: [WebGLTexture | null, WebGLTexture | null];
  pressureTextures: [WebGLTexture | null, WebGLTexture | null];
  divergenceTextures: [WebGLTexture | null, WebGLTexture | null];
  framebuffers: Record<string, WebGLFramebuffer | null>;
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Advection: semi-Lagrangian — sample backward along the velocity field.
const ADVECT_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uVelocity;
uniform sampler2D uSource;
uniform float uDt;

void main() {
  vec2 vel = texture(uVelocity, vUV).xy;
  // Step backward in time.
  vec2 prevPos = vUV - vel * uDt;
  outColor = texture(uSource, prevPos);
}
`;

// Compute divergence of velocity.
const DIVERGENCE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uVelocity;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uVelocity, 0));
  float L = texture(uVelocity, vUV - vec2(texel.x, 0.0)).x;
  float R = texture(uVelocity, vUV + vec2(texel.x, 0.0)).x;
  float B = texture(uVelocity, vUV - vec2(0.0, texel.y)).y;
  float T = texture(uVelocity, vUV + vec2(0.0, texel.y)).y;
  // Centered divergence.
  float div = 0.5 * (R - L + T - B);
  outColor = vec4(div, 0.0, 0.0, 1.0);
}
`;

// Pressure Poisson iteration: p_new = (p_left + p_right + p_up + p_down - div) / 4.
const PRESSURE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uPressure;
uniform sampler2D uDivergence;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uPressure, 0));
  float L = texture(uPressure, vUV - vec2(texel.x, 0.0)).x;
  float R = texture(uPressure, vUV + vec2(texel.x, 0.0)).x;
  float B = texture(uPressure, vUV - vec2(0.0, texel.y)).x;
  float T = texture(uPressure, vUV + vec2(0.0, texel.y)).x;
  float div = texture(uDivergence, vUV).x;
  outColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);
}
`;

// Project velocity: subtract pressure gradient.
const PROJECT_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uVelocity;
uniform sampler2D uPressure;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uVelocity, 0));
  float pL = texture(uPressure, vUV - vec2(texel.x, 0.0)).x;
  float pR = texture(uPressure, vUV + vec2(texel.x, 0.0)).x;
  float pB = texture(uPressure, vUV - vec2(0.0, texel.y)).x;
  float pT = texture(uPressure, vUV + vec2(0.0, texel.y)).x;
  vec2 vel = texture(uVelocity, vUV).xy;
  vel -= 0.5 * vec2(pR - pL, pT - pB);
  outColor = vec4(vel, 0.0, 1.0);
}
`;

// Splat: add a Gaussian at a position.
const SPLAT_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uTarget;
uniform vec2 uPoint;
uniform vec3 uColor;
uniform float uRadius;

void main() {
  vec2 p = vUV - uPoint;
  p.x *= 1.0;
  float d = exp(-dot(p, p) / uRadius);
  vec3 base = texture(uTarget, vUV).xyz;
  outColor = vec4(base + d * uColor, 1.0);
}
`;

// Splat velocity.
const SPLAT_VEL_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uTarget;
uniform vec2 uPoint;
uniform vec2 uVel;
uniform float uRadius;

void main() {
  vec2 p = vUV - uPoint;
  p.x *= 1.0;
  float d = exp(-dot(p, p) / uRadius);
  vec3 base = texture(uTarget, vUV).xyz;
  outColor = vec4(base.xy + d * uVel, 0.0, 1.0);
}
`;

// Render dye to screen.
const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uDye;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

vec3 palette(float t) {
  t = fract(t);
  return vec3(
    0.5 + 0.5 * sin(6.2831853 * t),
    0.5 + 0.5 * sin(6.2831853 * t + 2.094),
    0.5 + 0.5 * sin(6.2831853 * t + 4.189)
  );
}

void main() {
  vec3 dye = texture(uDye, vUV).xyz;
  // Sample dye from a few offsets for a glowing trail look.
  float density = max(dye.x, max(dye.y, dye.z));
  if (density < 0.001) {
    outColor = vec4(0.02, 0.02, 0.06, 1.0);
    return;
  }
  vec3 color = palette(density * 4.0);
  // Brighten with density.
  color *= 1.0 + density * 2.0;
  // Audio pulse.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  color *= 1.0 + bassAvg * 0.5;
  // Vignette.
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  float vig = 1.0 - length(p) * 0.4;
  color *= clamp(vig, 0.0, 1.0);
  outColor = vec4(color, 1.0);
}
`;

function createEmptyTexture(gl: WebGL2RenderingContext, size: number, channels: 3 | 4 = 4): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Uint8Array(size * size * channels);
  const format = channels === 4 ? gl.RGBA : gl.RGB;
  gl.texImage2D(gl.TEXTURE_2D, 0, format, size, size, 0, format, gl.UNSIGNED_BYTE, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function createFramebuffer(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createStableFluidPipeline(gl: WebGL2RenderingContext, size = 128): StableFluidPipeline | null {
  const programs: Record<string, WebGLProgram | null> = {
    advect: linkProgram(gl, VERT_SRC, ADVECT_FRAG_SRC, POST_ATTRIBS),
    divergence: linkProgram(gl, VERT_SRC, DIVERGENCE_FRAG_SRC, POST_ATTRIBS),
    pressure: linkProgram(gl, VERT_SRC, PRESSURE_FRAG_SRC, POST_ATTRIBS),
    project: linkProgram(gl, VERT_SRC, PROJECT_FRAG_SRC, POST_ATTRIBS),
    splat: linkProgram(gl, VERT_SRC, SPLAT_FRAG_SRC, POST_ATTRIBS),
    splatVel: linkProgram(gl, VERT_SRC, SPLAT_VEL_FRAG_SRC, POST_ATTRIBS),
    render: linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS),
  };
  for (const k in programs) {
    if (!programs[k]) return null;
  }
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const vel0 = createEmptyTexture(gl, size, 3);
  const vel1 = createEmptyTexture(gl, size, 3);
  const dye0 = createEmptyTexture(gl, size, 3);
  const dye1 = createEmptyTexture(gl, size, 3);
  const p0 = createEmptyTexture(gl, size, 3);
  const p1 = createEmptyTexture(gl, size, 3);
  const div0 = createEmptyTexture(gl, size, 3);
  const div1 = createEmptyTexture(gl, size, 3);

  const framebuffers: Record<string, WebGLFramebuffer | null> = {
    vel0: createFramebuffer(gl, vel0, size),
    vel1: createFramebuffer(gl, vel1, size),
    dye0: createFramebuffer(gl, dye0, size),
    dye1: createFramebuffer(gl, dye1, size),
    p0: createFramebuffer(gl, p0, size),
    p1: createFramebuffer(gl, p1, size),
    div0: createFramebuffer(gl, div0, size),
    div1: createFramebuffer(gl, div1, size),
  };

  return {
    programs,
    vao,
    vbo,
    velTextures: [vel0, vel1],
    dyeTextures: [dye0, dye1],
    pressureTextures: [p0, p1],
    divergenceTextures: [div0, div1],
    framebuffers,
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

function setUniform1i(gl: WebGL2RenderingContext, prog: WebGLProgram | null, name: string, val: number) {
  if (!prog) return;
  const loc = gl.getUniformLocation(prog, name);
  if (loc) gl.uniform1i(loc, val);
}

function setUniform2f(gl: WebGL2RenderingContext, prog: WebGLProgram | null, name: string, x: number, y: number) {
  if (!prog) return;
  const loc = gl.getUniformLocation(prog, name);
  if (loc) gl.uniform2f(loc, x, y);
}

function setUniform3f(gl: WebGL2RenderingContext, prog: WebGLProgram | null, name: string, x: number, y: number, z: number) {
  if (!prog) return;
  const loc = gl.getUniformLocation(prog, name);
  if (loc) gl.uniform3f(loc, x, y, z);
}

function setUniform1f(gl: WebGL2RenderingContext, prog: WebGLProgram | null, name: string, val: number) {
  if (!prog) return;
  const loc = gl.getUniformLocation(prog, name);
  if (loc) gl.uniform1f(loc, val);
}

function drawFullscreen(gl: WebGL2RenderingContext, vao: WebGLVertexArrayObject | null, program: WebGLProgram | null) {
  if (!vao || !program) return;
  gl.useProgram(program);
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export function computeStableFluidBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeStableFluidRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawStableFluid(
  gl: WebGL2RenderingContext,
  pipe: StableFluidPipeline,
  width: number,
  height: number,
  t: number,
): void {
  const size = pipe.gridSize;
  // Splat some velocity and dye at center and a few random points.
  // Use a couple of moving splats based on time + audio.
  const bx = 0.5 + 0.3 * Math.sin(t * 0.5);
  const by = 0.5 + 0.3 * Math.cos(t * 0.4);
  // Splat velocity.
  const splatVelProg = pipe.programs.splatVel;
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.vel1);
  gl.viewport(0, 0, size, size);
  gl.useProgram(splatVelProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, splatVelProg, "uTarget", 0);
  setUniform2f(gl, splatVelProg, "uPoint", bx, by);
  setUniform2f(gl, splatVelProg, "uVel", 0.5 * Math.cos(t), 0.5 * Math.sin(t));
  setUniform1f(gl, splatVelProg, "uRadius", 0.02);
  drawFullscreen(gl, pipe.vao, splatVelProg);
  pipe.velTextures = [pipe.velTextures[1], pipe.velTextures[0]] as [WebGLTexture | null, WebGLTexture | null];
  pipe.framebuffers.vel0 = pipe.framebuffers.vel1;
  // Repeat to swap correctly
  const tmpVel = pipe.velTextures[0];
  pipe.velTextures = [pipe.velTextures[1], tmpVel];
  pipe.framebuffers.vel0 = pipe.framebuffers.vel1;
  pipe.framebuffers.vel1 = createFramebuffer(gl, pipe.velTextures[0]!, size);

  // Splat dye at the same location.
  const splatProg = pipe.programs.splat;
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.dye1);
  gl.viewport(0, 0, size, size);
  gl.useProgram(splatProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.dyeTextures[0]);
  setUniform1i(gl, splatProg, "uTarget", 0);
  setUniform2f(gl, splatProg, "uPoint", bx, by);
  // Color cycles through hues.
  const hue = (t * 0.1) % 1;
  const r = 0.5 + 0.5 * Math.sin(hue * 6.28);
  const g = 0.5 + 0.5 * Math.sin(hue * 6.28 + 2.094);
  const b = 0.5 + 0.5 * Math.sin(hue * 6.28 + 4.189);
  setUniform3f(gl, splatProg, "uColor", r * 0.3, g * 0.3, b * 0.3);
  setUniform1f(gl, splatProg, "uRadius", 0.015);
  drawFullscreen(gl, pipe.vao, splatProg);
  // Swap dye textures.
  const tmpDye = pipe.dyeTextures[0];
  pipe.dyeTextures = [pipe.dyeTextures[1], tmpDye];
  pipe.framebuffers.dye0 = pipe.framebuffers.dye1;
  pipe.framebuffers.dye1 = createFramebuffer(gl, pipe.dyeTextures[0]!, size);

  // Advect velocity by itself.
  const advectProg = pipe.programs.advect;
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.vel1);
  gl.viewport(0, 0, size, size);
  gl.useProgram(advectProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, advectProg, "uVelocity", 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, advectProg, "uSource", 1);
  setUniform1f(gl, advectProg, "uDt", 0.01);
  drawFullscreen(gl, pipe.vao, advectProg);
  const tmpV = pipe.velTextures[0];
  pipe.velTextures = [pipe.velTextures[1], tmpV];
  pipe.framebuffers.vel0 = pipe.framebuffers.vel1;
  pipe.framebuffers.vel1 = createFramebuffer(gl, pipe.velTextures[0]!, size);

  // Compute divergence.
  const divProg = pipe.programs.divergence;
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.div0);
  gl.viewport(0, 0, size, size);
  gl.useProgram(divProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, divProg, "uVelocity", 0);
  drawFullscreen(gl, pipe.vao, divProg);

  // Pressure Jacobi iterations.
  const pressureProg = pipe.programs.pressure;
  for (let iter = 0; iter < 8; iter++) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.p1);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pressureProg);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, pipe.pressureTextures[0]);
    setUniform1i(gl, pressureProg, "uPressure", 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, pipe.divergenceTextures[0]);
    setUniform1i(gl, pressureProg, "uDivergence", 1);
    drawFullscreen(gl, pipe.vao, pressureProg);
    const tmpP = pipe.pressureTextures[0];
    pipe.pressureTextures = [pipe.pressureTextures[1], tmpP];
    pipe.framebuffers.p0 = pipe.framebuffers.p1;
    pipe.framebuffers.p1 = createFramebuffer(gl, pipe.pressureTextures[0]!, size);
  }

  // Project velocity.
  const projectProg = pipe.programs.project;
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.vel1);
  gl.viewport(0, 0, size, size);
  gl.useProgram(projectProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, projectProg, "uVelocity", 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, pipe.pressureTextures[0]);
  setUniform1i(gl, projectProg, "uPressure", 1);
  drawFullscreen(gl, pipe.vao, projectProg);
  const tmpV2 = pipe.velTextures[0];
  pipe.velTextures = [pipe.velTextures[1], tmpV2];
  pipe.framebuffers.vel0 = pipe.framebuffers.vel1;
  pipe.framebuffers.vel1 = createFramebuffer(gl, pipe.velTextures[0]!, size);

  // Advect dye by velocity.
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.framebuffers.dye1);
  gl.viewport(0, 0, size, size);
  gl.useProgram(advectProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.velTextures[0]);
  setUniform1i(gl, advectProg, "uVelocity", 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, pipe.dyeTextures[0]);
  setUniform1i(gl, advectProg, "uSource", 1);
  setUniform1f(gl, advectProg, "uDt", 0.01);
  drawFullscreen(gl, pipe.vao, advectProg);
  const tmpD = pipe.dyeTextures[0];
  pipe.dyeTextures = [pipe.dyeTextures[1], tmpD];
  pipe.framebuffers.dye0 = pipe.framebuffers.dye1;
  pipe.framebuffers.dye1 = createFramebuffer(gl, pipe.dyeTextures[0]!, size);

  // Render dye to screen.
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  const renderProg = pipe.programs.render;
  gl.useProgram(renderProg);
  setUniform1i(gl, renderProg, "uDye", 0);
  setUniform2f(gl, renderProg, "uResolution", width, height);
  setUniform1f(gl, renderProg, "uTime", t);
  setUniform3f(gl, renderProg, "uBass", pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  setUniform3f(gl, renderProg, "uMid", pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  setUniform3f(gl, renderProg, "uHigh", pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.dyeTextures[0]);
  drawFullscreen(gl, pipe.vao, renderProg);
}
